import {
    getSourceTypes,
    getPartners,
    createPartner,
    updatePartner,
    deletePartner,
    getPartnerLinks,
    createPartnerLink,
    deletePartnerLink,
    getPartnerLinkFunnel,
    getPartnerCohortStats,
    downloadCohortCsv,
} from '@/api/partners.js';

export default {
    state: {
        partners: [],
        isPartnersLoading: false,
        sourceTypes: [],
        selectedPartner: null,
        partnerLinks: [],
        isLinksLoading: false,
        funnelData: null,
        isFunnelLoading: false,
        cohortStats: null,
        isCohortLoading: false,
        partnerLinkFunnels: {}, // { [linkId]: funnelData } — aggregate source
    },

    getters: {
        PARTNERS: (state) => state.partners,
        IS_PARTNERS_LOADING: (state) => state.isPartnersLoading,
        SOURCE_TYPES: (state) => state.sourceTypes,
        SELECTED_PARTNER: (state) => state.selectedPartner,
        PARTNER_LINKS: (state) => state.partnerLinks,
        IS_LINKS_LOADING: (state) => state.isLinksLoading,
        FUNNEL_DATA: (state) => state.funnelData,
        IS_FUNNEL_LOADING: (state) => state.isFunnelLoading,
        COHORT_STATS: (state) => state.cohortStats,
        IS_COHORT_LOADING: (state) => state.isCohortLoading,
        AGGREGATE_FUNNEL: (state) => {
            const funnels = Object.values(state.partnerLinkFunnels);
            if (!funnels.length) return null;
            // Collect all known step names in canonical order
            const ORDER = ['traffic', 'visit', 'signup', 'first_action', 'core_action', 'retention_7d', 'retention_30d'];
            const totals = {};
            funnels.forEach((f) => {
                if (!f || !f.steps) return;
                f.steps.forEach((s) => {
                    totals[s.name] = (totals[s.name] || 0) + s.count;
                });
            });
            const steps = ORDER.filter((n) => n in totals).map((name) => ({ name, count: totals[name] }));
            console.debug(`[store] AGGREGATE_FUNNEL computed from ${funnels.length} links`, steps);
            return steps.length ? { steps } : null;
        },
    },

    mutations: {
        SET_PARTNERS(state, partners) {
            state.partners = partners;
        },
        SET_SOURCE_TYPES(state, types) {
            state.sourceTypes = types;
        },
        SET_IS_PARTNERS_LOADING(state, val) {
            state.isPartnersLoading = val;
        },
        SET_SELECTED_PARTNER(state, partner) {
            state.selectedPartner = partner;
        },
        SET_PARTNER_LINKS(state, links) {
            state.partnerLinks = links;
        },
        SET_IS_LINKS_LOADING(state, val) {
            state.isLinksLoading = val;
        },
        SET_FUNNEL_DATA(state, data) {
            state.funnelData = data;
        },
        SET_IS_FUNNEL_LOADING(state, val) {
            state.isFunnelLoading = val;
        },
        SET_COHORT_STATS(state, data) {
            state.cohortStats = data;
        },
        SET_IS_COHORT_LOADING(state, val) {
            state.isCohortLoading = val;
        },
        SET_LINK_FUNNEL(state, { linkId, data }) {
            state.partnerLinkFunnels = { ...state.partnerLinkFunnels, [linkId]: data };
        },
        CLEAR_LINK_FUNNELS(state) {
            state.partnerLinkFunnels = {};
        },
    },

    actions: {
        async loadSourceTypes({ commit, state }) {
            if (state.sourceTypes.length > 0) return;
            const types = await getSourceTypes();
            commit('SET_SOURCE_TYPES', types);
        },

        async loadPartners({ commit }) {
            commit('SET_IS_PARTNERS_LOADING', true);
            try {
                const data = await getPartners();
                commit('SET_PARTNERS', data);
            } finally {
                commit('SET_IS_PARTNERS_LOADING', false);
            }
        },

        async addPartner({ dispatch }, partnerData) {
            const partner = await createPartner(partnerData);
            await dispatch('loadPartners');
            return partner;
        },

        async editPartner({ dispatch }, { partnerId, data }) {
            await updatePartner(partnerId, data);
            await dispatch('loadPartners');
        },

        async removePartner({ dispatch }, partnerId) {
            await deletePartner(partnerId);
            await dispatch('loadPartners');
        },

        async togglePartnerActive({ dispatch }, { partnerId, isActive }) {
            console.debug(`[store] togglePartnerActive partner_id=${partnerId} is_active=${isActive}`);
            await updatePartner(partnerId, { is_active: isActive });
            await dispatch('loadPartners');
            console.debug(`[store] togglePartnerActive done partner_id=${partnerId}`);
        },

        async loadPartnerLinks({ commit }, partnerId) {
            commit('SET_IS_LINKS_LOADING', true);
            try {
                const data = await getPartnerLinks(partnerId);
                commit('SET_PARTNER_LINKS', data);
            } finally {
                commit('SET_IS_LINKS_LOADING', false);
            }
        },

        async addPartnerLink({ dispatch }, { partnerId, data }) {
            await createPartnerLink(partnerId, data);
            await dispatch('loadPartnerLinks', partnerId);
        },

        async removePartnerLink({ dispatch }, { partnerId, linkId }) {
            await deletePartnerLink(partnerId, linkId);
            await dispatch('loadPartnerLinks', partnerId);
        },

        async loadPartnerFunnel({ commit }, { partnerId, linkId, params = {} }) {
            commit('SET_IS_FUNNEL_LOADING', true);
            try {
                const data = await getPartnerLinkFunnel(partnerId, linkId, params);
                commit('SET_FUNNEL_DATA', data);
            } finally {
                commit('SET_IS_FUNNEL_LOADING', false);
            }
        },

        async loadAllLinkFunnels({ commit, state }, { partnerId }) {
            const links = state.partnerLinks;
            console.debug(`[store] loadAllLinkFunnels: loading funnels for ${links.length} links of partner_id=${partnerId}`);
            commit('CLEAR_LINK_FUNNELS');
            await Promise.all(
                links.map(async (link) => {
                    try {
                        const data = await getPartnerLinkFunnel(partnerId, link.id, {});
                        commit('SET_LINK_FUNNEL', { linkId: link.id, data });
                    } catch (e) {
                        console.warn(`[store] loadAllLinkFunnels: failed for link_id=${link.id}`, e);
                    }
                }),
            );
            console.debug(`[store] loadAllLinkFunnels: done for partner_id=${partnerId}`);
        },

        async loadPartnerCohortStats({ commit }, { partnerId, params = {} }) {
            commit('SET_IS_COHORT_LOADING', true);
            try {
                const data = await getPartnerCohortStats(partnerId, params);
                commit('SET_COHORT_STATS', data);
            } finally {
                commit('SET_IS_COHORT_LOADING', false);
            }
        },

        async exportPartnerCohortCsv(_, { partnerId, params = {} }) {
            const blob = await downloadCohortCsv(partnerId, params);
            if (!blob) return;
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `partner-${partnerId}-cohort.csv`;
            a.click();
            URL.revokeObjectURL(url);
        },
    },
};
